import { useEffect } from 'react';

const SITE_URL = 'https://nikovibe.com';

function upsertMeta(attr, key, content) {
  if (content == null || content === '') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Update document title + Open Graph / Twitter tags for the current view.
 * Social crawlers still need server-side HTML; this helps browser tabs and in-app webviews.
 */
export function usePageMeta({
  title,
  description,
  image,
  url,
  type = 'website',
  keywords,
  imageAlt
}) {
  useEffect(() => {
    if (!title) return undefined;

    const prevTitle = document.title;
    document.title = title;

    const shareImage = image || `${SITE_URL}/images/niko_vibe_logo.jpg`;

    upsertMeta('name', 'description', description);
    if (keywords) upsertMeta('name', 'keywords', keywords);

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', type);
    if (url) upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', shareImage);
    upsertMeta('property', 'og:image:url', shareImage);
    upsertMeta('property', 'og:image:secure_url', shareImage);
    if (imageAlt) upsertMeta('property', 'og:image:alt', imageAlt);
    upsertMeta('property', 'og:site_name', 'Niko Vibe');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', shareImage);
    if (imageAlt) upsertMeta('name', 'twitter:image:alt', imageAlt);

    if (url) upsertLink('canonical', url);

    let imageSrc = document.head.querySelector('link[rel="image_src"]');
    if (!imageSrc) {
      imageSrc = document.createElement('link');
      imageSrc.setAttribute('rel', 'image_src');
      document.head.appendChild(imageSrc);
    }
    imageSrc.setAttribute('href', shareImage);

    return () => {
      document.title = prevTitle;
    };
  }, [title, description, image, url, type, keywords, imageAlt]);
}

export function absoluteSiteUrl(path = '/') {
  if (!path) return SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

export function absoluteMediaUrl(url) {
  if (!url) return `${SITE_URL}/images/niko_vibe_logo.jpg`;
  if (/^https?:\/\//i.test(url)) {
    const upload = url.match(/(\/uploads\/.+)$/i);
    if (upload) return `${SITE_URL}${upload[1]}`;
    return url;
  }
  if (url.startsWith('/uploads/')) return `${SITE_URL}${url}`;
  if (url.startsWith('/')) return `${SITE_URL}${url}`;
  return `${SITE_URL}/images/niko_vibe_logo.jpg`;
}

export default usePageMeta;
